import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, BookOpen, Clock, Compass, Globe } from "lucide-react";

import { BLOG } from "@/data/blog";
import { CORNERSTONES_NL } from "@/data/cornerstones/nl";
import CountUp from "@/components/landing/CountUp";

// Locales supported across the site (must mirror cornerstones/index.js + i18n/index.js).
const LOCALE_COUNT = 26;

/**
 * Compute the average "freshness" across cornerstone clusters:
 * for each cluster, take max(dateModified || datePublished). Then take the
 * average days-since-update across all clusters. Single number summarises
 * how recently *any* cluster has been touched.
 */
function computeFreshnessDays() {
  const today = new Date();
  const clusterMaxDates = {};
  Object.values(BLOG).forEach((b) => {
    const path = b.cornerstone_link?.path;
    if (!path) return;
    const d = b.dateModified || b.datePublished;
    if (!d) return;
    if (!clusterMaxDates[path] || d > clusterMaxDates[path]) {
      clusterMaxDates[path] = d;
    }
  });
  const maxDates = Object.values(clusterMaxDates);
  if (maxDates.length === 0) return null;
  const ages = maxDates.map((iso) => {
    const ms = today - new Date(iso + "T00:00:00Z");
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  });
  return Math.round(ages.reduce((s, a) => s + a, 0) / ages.length);
}

/**
 * Split a localized stat value like "1d", "166", "+12" into a numeric portion
 * and surrounding prefix/suffix so we can animate the digits via CountUp.
 * Returns `null` if no number is present (fallback to static rendering).
 */
function splitNumeric(text) {
  if (typeof text !== "string") return null;
  const match = text.match(/^(\D*?)(\d[\d.,]*)(.*)$/);
  if (!match) return null;
  const numeric = parseInt(match[2].replace(/[.,]/g, ""), 10);
  if (Number.isNaN(numeric)) return null;
  return { prefix: match[1], value: numeric, suffix: match[3] };
}

const StatCard = ({ icon: Icon, value, label, sub, testid, animate = true }) => {
  const parts = animate ? splitNumeric(value) : null;
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-6 transition-all hover:-translate-y-1 hover:border-[hsl(var(--forest))]/30"
    >
      <div className="flex items-center gap-2 text-[hsl(var(--forest))]">
        <Icon className="h-5 w-5" strokeWidth={1.7} />
        {parts ? (
          <CountUp
            value={parts.value}
            prefix={parts.prefix}
            suffix={parts.suffix}
            testid={`${testid}-number`}
            className="font-display text-4xl text-[hsl(var(--ink))] sm:text-5xl"
          />
        ) : (
          <p className="font-display text-4xl text-[hsl(var(--ink))] sm:text-5xl">
            {value}
          </p>
        )}
      </div>
      <p className="mt-3 text-sm font-medium text-[hsl(var(--ink))]">{label}</p>
      <p className="mt-1 text-xs leading-relaxed text-[hsl(var(--ink-soft))]">{sub}</p>
    </div>
  );
};

export const ContentStats = () => {
  const { t } = useTranslation();

  // Memoized so React doesn't recompute these on every render.
  const stats = useMemo(() => {
    const articles = Object.keys(BLOG).length;
    // Cornerstone routes minus the "ui" + "gidsen" hub container → these are the actual buying guides.
    const guides = Object.keys(CORNERSTONES_NL).filter(
      (k) => k !== "ui" && k !== "gidsen"
    ).length;
    const freshness = computeFreshnessDays();
    return { articles, guides, freshness };
  }, []);

  const interpolate = (tmpl, vars) => {
    if (!tmpl) return "";
    return Object.keys(vars).reduce(
      (acc, k) => acc.replace(`{{${k}}}`, vars[k]),
      tmpl
    );
  };

  return (
    <section
      data-testid="content-stats-section"
      className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]"
    >
      <div className="mx-auto max-w-6xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            {t("contentStats.eyebrow")}
          </p>
          <h2 className="mt-3 font-display text-3xl font-medium leading-[1.1] text-[hsl(var(--ink))] sm:text-4xl lg:text-5xl">
            {t("contentStats.title")}
          </h2>
          <p className="mt-4 text-base text-[hsl(var(--ink-soft))] sm:text-lg">
            {t("contentStats.lead")}
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            testid="stat-articles"
            icon={BookOpen}
            value={interpolate(t("contentStats.stats.articles.value"), {
              count: stats.articles,
            })}
            label={t("contentStats.stats.articles.label")}
            sub={t("contentStats.stats.articles.sub")}
          />
          <StatCard
            testid="stat-languages"
            icon={Globe}
            value={interpolate(t("contentStats.stats.languages.value"), {
              count: LOCALE_COUNT,
            })}
            label={t("contentStats.stats.languages.label")}
            sub={t("contentStats.stats.languages.sub")}
          />
          <StatCard
            testid="stat-guides"
            icon={Compass}
            value={interpolate(t("contentStats.stats.guides.value"), {
              count: stats.guides,
            })}
            label={t("contentStats.stats.guides.label")}
            sub={t("contentStats.stats.guides.sub")}
          />
          <StatCard
            testid="stat-freshness"
            icon={Clock}
            value={
              stats.freshness === null
                ? "—"
                : interpolate(t("contentStats.stats.freshness.value"), {
                    days: stats.freshness,
                  })
            }
            label={t("contentStats.stats.freshness.label")}
            sub={t("contentStats.stats.freshness.sub")}
          />
        </div>

        <Link
          to="/gidsen"
          data-testid="content-stats-cta"
          className="mt-10 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--forest))] px-6 py-3 text-sm font-medium text-[hsl(var(--background))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--forest-dark))]"
        >
          {t("contentStats.cta")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
};

export default ContentStats;
